import React from 'react';
import {Navigate, Route} from 'react-router';
import {SentryRoutes} from '../Sentry';
import {QueryClient, QueryClientProvider} from '@tanstack/react-query';

import UnauthenticatedNavBar from '../Nav/UnauthenticatedNavBar';

import {useAuth} from '../Auth/useAuth';
import {OnboardingProvider} from '../Onboarding/OnboardingProvider';
import ProOnboarding from './Onboarding/ProOnboarding';
import {DIALOG_URL_PARAMS} from '../Util/constants';
import ProLogo from '../img/logo/logo_professional.svg?react';
import ProLogoDark from '../img/logo/logo_professional_dark.svg?react';
import {usePageTracking, COMPASS_APPLICATIONS} from '../PostHogAnalytics';

import ForgotPassword from '../Auth/ForgotPassword';
import ResetPassword from '../Auth/ResetPassword';
import Login from '../Auth/Login';
import ProRegisterScreen from './ProRegisterScreen';
import ProHome from './ProHome';
import ProAccount from './ProAccount';

import ProTimeline from './ProTimeline';
import ContactUs from '../ContactUs';
import useDialogQuery from '../Util/useDialogQuery';
import GlossaryDialog from '../Glossary/GlossaryDialog';
import { DefinitionModalProvider } from '../Glossary/DefinitionModalProvider';
import ProViewAction from './ProViewAction';
import ProParentCategories from './ProParentCategories';
import ProYoungPersonCategories from './ProYoungPersonCategories';
import ProYoungPersonViewCategory from './ProYoungPersonViewCategory';
import ProParentViewCategory from './ProParentViewCategory';
import ProViewTopic from './ProViewTopic';
import EditAccountScreen from '../Account/EditAccountScreen';
import ProAccountJobTitle from './ProAccountJobTitle';
import ProAccountSector from './ProAccountSector';
import ProAccountArea from './ProAccountArea';
import DeleteAccount from '../Account/DeleteAccount';
import ProPrivacyNotice from './ProPrivacyNotice';
import CookieConsentBanner from '../Util/CookieConsentBanner';
import ProKeyDuties from './ProKeyDuties';
import ProKeyDutiesStage from './ProKeyDutiesStage';
import ProViewKeyDuty from './ProViewKeyDuty';
import {StoreRedirectUrlForAuth} from '../Util/StoreRedirectUrlForAuth';
import AppBannerMessage from '../Util/AppBannerMessage';
import Layout from '@/src/Pro/Layout';
import PublicShareView from '../Shared/PublicShareView';
import ShareManager from '../Shared/ShareResource/ShareManager';

const App = () => {
  const {authState} = useAuth();

  usePageTracking(COMPASS_APPLICATIONS.PROFESSIONAL);

  return (
    <>
      <AppBannerMessage />
      {authState.isAuthenticated ? null : (
        <UnauthenticatedNavBar LogoLight={ProLogo} LogoDark={ProLogoDark} />
      )}
      <main>
        {authState.isCheckingAuth ? (
          <div>Loading…</div>
        ) : authState.isAuthenticated ? (
          <AuthenticatedApp />
        ) : (
          <UnauthenticatedApp />
        )}
      </main>
      <CookieConsentBanner />
    </>
  );
};
// A refused read (403) is not asked for again: the answer will not change, so its error
// shows at once. Any other failure keeps React Query's three retries (COM-427).
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => error?.response?.status !== 403 && failureCount < 3,
    },
  },
});

const AuthenticatedApp = () => {
  const {dialogQuery} = useDialogQuery();
  return (
    <QueryClientProvider client={queryClient}>
      <DefinitionModalProvider>
        <OnboardingProvider Onboarding={ProOnboarding}>
          <SentryRoutes>
          <Route element={<Layout />}>
            <Route path="/dashboard" element={<ProHome />} />
            <Route path="/timeline" element={<ProTimeline />} />
            <Route path="/parent-categories" element={<ProParentCategories />} />
            <Route path="/parent-categories/:category" element={<ProParentViewCategory />} />
            <Route
              path="/parent-categories/:category/item/:action"
              element={<ProViewAction />}
            />
            <Route path="/young-person-categories" element={<ProYoungPersonCategories />} />
            <Route
              path="/young-person-categories/:category"
              element={<ProYoungPersonViewCategory />}
            />
            <Route
              path="/young-person-categories/:category/item/:topic"
              element={<ProViewTopic />}
            />
            <Route path="/key-duties" element={<ProKeyDuties />} />
            <Route path="/key-duties/stage/:stage" element={<ProKeyDutiesStage />} />
            <Route path="/key-duties/stage/:stage/duty/:duty" element={<ProViewKeyDuty />} />
            <Route path="/manage-shares" element={<ShareManager padded={false} />} />
            <Route path="/my-account" element={<ProAccount />} />
            <Route path="/my-account/delete" element={<DeleteAccount />} />
            <Route
              path="/my-account/edit/*"
              element={
                <EditAccountScreen
                  profileDetails={
                    <>
                      <Route path="job-title" element={<ProAccountJobTitle />} />
                      <Route path="sector" element={<ProAccountSector />} />
                      <Route path="area" element={<ProAccountArea />} />
                      <Route path="*" element={<Navigate to="/my-account" />} />
                    </>
                  }
                />
              }
            />
            <Route path="/contact-us" element={<ContactUs />} />
            <Route path="/privacy" element={<ProPrivacyNotice />} />
          </Route>
          <Route path="/shared/:token" element={<PublicShareView />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </SentryRoutes>
          {dialogQuery === DIALOG_URL_PARAMS.GLOSSARY && <GlossaryDialog />}
        </OnboardingProvider>
      </DefinitionModalProvider>
    </QueryClientProvider>
  );
};

const UnauthenticatedApp = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <SentryRoutes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<ProRegisterScreen />} />
        <Route path="/forgotpassword" element={<ForgotPassword />} />
        <Route path="/resetpassword" element={<ResetPassword />} />
        <Route path="/privacy" element={<ProPrivacyNotice />} />
        <Route path="/shared/:token" element={<PublicShareView />} />
        <Route path="*" element={<StoreRedirectUrlForAuth />} />
      </SentryRoutes>
    </QueryClientProvider>
  );
};

export default App;
